import { z } from 'zod';

import { IdSchema, IsoDateSchema } from './common.js';

export const MuscleGroupSchema = z.enum([
  'chest',
  'back',
  'shoulders',
  'arms',
  'legs',
  'core',
  'cardio',
  'other',
]);
export type MuscleGroup = z.infer<typeof MuscleGroupSchema>;

export const SubMuscleGroupSchema = z.enum([
  'upper_chest',
  'mid_chest',
  'lower_chest',
  'lats',
  'upper_back',
  'lower_back',
  'traps',
  'front_delts',
  'side_delts',
  'rear_delts',
  'short_head',
  'long_head',
  'brachialis',
  'long_head_triceps',
  'lateral_head',
  'medial_head',
  'forearms',
  'quads',
  'vastus_medialis',
  'hamstrings',
  'glutes',
  'gastrocnemius',
  'soleus',
  'adductors',
  'upper_abs',
  'lower_abs',
  'obliques',
  'cardio',
]);
export type SubMuscleGroup = z.infer<typeof SubMuscleGroupSchema>;

export const EquipmentSchema = z.enum([
  'barbell',
  'dumbbell',
  'machine',
  'cable',
  'bodyweight',
  'other',
]);
export type Equipment = z.infer<typeof EquipmentSchema>;

export const ExerciseTypeSchema = z.enum(['weight_reps', 'bodyweight_reps', 'duration', 'cardio']);
export type ExerciseType = z.infer<typeof ExerciseTypeSchema>;

export const ExerciseCategorySchema = z.enum(['compound', 'isolation', 'cardio']);
export type ExerciseCategory = z.infer<typeof ExerciseCategorySchema>;

export const ExerciseSchema = z.object({
  id: z.string().min(1).max(128),
  name: z.string().min(1).max(200),
  primaryMuscle: MuscleGroupSchema,
  secondaryMuscles: z.array(MuscleGroupSchema).default([]),
  equipment: EquipmentSchema,
  type: ExerciseTypeSchema,
  category: ExerciseCategorySchema,
  instructions: z.array(z.string().max(1000)).max(10).default([]),
  // Optional on the shared shape so CustomExerciseSchema (which extends this)
  // does not require it — CustomExercise rows are not tagged. Catalog rows
  // always return a value because the DB column is non-null.
  subMuscleGroup: SubMuscleGroupSchema.optional(),
});
export type Exercise = z.infer<typeof ExerciseSchema>;

export const CustomExerciseSchema = ExerciseSchema.extend({
  id: IdSchema,
  userId: IdSchema,
  createdAt: IsoDateSchema,
});
export type CustomExercise = z.infer<typeof CustomExerciseSchema>;

export const CreateCustomExerciseSchema = ExerciseSchema.omit({ id: true });
export type CreateCustomExerciseInput = z.infer<typeof CreateCustomExerciseSchema>;

export const ExerciseQuerySchema = z.object({
  q: z.string().max(120).optional(),
  muscle: MuscleGroupSchema.optional(),
  equipment: EquipmentSchema.optional(),
});
export type ExerciseQuery = z.infer<typeof ExerciseQuerySchema>;
